import React, { useContext, useState } from "react";
import { t } from "ttag";

import { Context } from "@reactivated";

import { CRMForm, FormType } from ".";
import { ButtonPrimary, ButtonSecondary } from "../Clickables";
import { AutocompleteField } from "../Forms/Autocomplete";
import { DateField } from "../Forms/Date";
import { FileUploadField } from "../Forms/FileUpload";
import { TextField } from "../Forms/Input";
import { Select, SelectItem } from "../Forms/Select";
import { FormLayout, Legal } from "./CRMForm";
import { StateSelect } from "./StateSelect";

import styles from "./forms.module.scss";

interface Props extends FormLayout {
    product_autocomplete: Array<{ title: string; sku: string }>;
    retailers: string[];
}

export const ProductRegistrationCAForm = (props: Props) => {
    const context = useContext(Context);
    const [productCount, setProductCount] = useState(1);

    const addProduct = () => {
        setProductCount((prev) => prev + 1);
    };

    const removeProduct = () => {
        setProductCount((prev) => Math.max(1, prev - 1));
    };

    return (
        <CRMForm
            layout={props.layout}
            type={FormType.PRODUCT_REGISTRATION_CA}
            custom_submit_url={props.custom_submit_url}
        >
            {(disabled) => (
                <>
                    {Array.from({ length: productCount }).map((_, index) => {
                        const productNum = index + 1;
                        return (
                            <div key={index} className={styles.productSection}>
                                {index > 0 && (
                                    <hr className={styles.productDivider} />
                                )}

                                <div className={styles.productHeader}>
                                    <h3 className={styles.productTitle}>
                                        {t`Product ${productNum}`}
                                    </h3>
                                    {index > 0 && (
                                        <ButtonSecondary
                                            onPress={removeProduct}
                                            type="button"
                                        >
                                            {t`Remove Product`}
                                        </ButtonSecondary>
                                    )}
                                </div>

                                <AutocompleteField
                                    label={t`Model`}
                                    name="Model[]"
                                    isRequired
                                    items={props.product_autocomplete}
                                    itemKey="sku"
                                    itemFormatter={(item) =>
                                        t`${item.sku}: ${item.title}`
                                    }
                                    placeholder={t`Search by product name or model #`}
                                    className={styles.input}
                                />
                                <TextField
                                    label={t`Serial Number`}
                                    className={styles.input}
                                    name="SerialNumber[]"
                                />
                                <Select
                                    label={t`Purchased From`}
                                    name="PurchasedFrom[]"
                                    isRequired
                                    placeholder={t`Choose One`}
                                    className={styles.input}
                                >
                                    {props.retailers.map((retailer) => (
                                        <SelectItem
                                            key={retailer}
                                            id={retailer}
                                        >
                                            {retailer}
                                        </SelectItem>
                                    ))}
                                </Select>
                                <DateField
                                    name="DateOfPurchase[]"
                                    label={t`Date of Purchase`}
                                    className={styles.input}
                                    isRequired
                                />
                                <DateField
                                    name="DateOfDelivery[]"
                                    label={t`Date of Delivery`}
                                    className={styles.input}
                                />
                                <FileUploadField
                                    className={styles.input}
                                    name="UploadReceiptImage[]"
                                    label={t`Upload Receipt Image`}
                                    acceptedFileTypes={[
                                        "image/jpeg",
                                        "image/png",
                                        "application/pdf",
                                    ]}
                                    isRequired
                                />
                                <FileUploadField
                                    name="UploadInstalledProductImage[]"
                                    label={t`Upload Installed Product Image`}
                                    acceptedFileTypes={[
                                        "image/jpeg",
                                        "image/png",
                                        "application/pdf",
                                    ]}
                                    className={styles.input}
                                />
                            </div>
                        );
                    })}

                    <div className={styles.addProductContainer}>
                        <ButtonSecondary
                            onPress={addProduct}
                            type="button"
                            icon="plus"
                        >
                            {t`Add Another Product`}
                        </ButtonSecondary>
                    </div>

                    <TextField
                        label={t`Full Name`}
                        className={styles.input}
                        name="FullName"
                        isRequired
                    />
                    <TextField
                        label={t`Email Address`}
                        className={styles.input}
                        name="EmailAddress"
                        isRequired
                        type="email"
                    />
                    <TextField
                        label={t`Phone Number`}
                        className={styles.input}
                        name="PhoneNumber"
                        isRequired
                    />
                    <TextField
                        label={t`Address`}
                        className={styles.input}
                        name="StreetAddress"
                        isRequired
                    />
                    <TextField
                        label={t`Apt / Ste`}
                        className={styles.input}
                        name="Apt"
                    />
                    <TextField
                        label={t`City`}
                        className={styles.input}
                        name="City"
                        isRequired
                    />
                    <div className={styles.groupedFields}>
                        <StateSelect className={styles.input} name="Province" />
                        <TextField
                            label={t`ZIP Code`}
                            className={styles.input}
                            name="PostalCode"
                            isRequired
                        />
                    </div>
                    <Legal href={context.wagtail_links.privacy_policy} />
                    <div className={styles.submitContainer}>
                        <ButtonPrimary type="submit" disabled={disabled}>
                            {t`Submit`}
                        </ButtonPrimary>
                    </div>
                </>
            )}
        </CRMForm>
    );
};
